import { useState } from 'react';
import { CheckCircle2, Eye, FileText, History, Send, Undo2, X } from 'lucide-react';
import {
  getReportSnapshotContent,
  snapshotState,
  useReportSnapshots,
  usePublishReportSnapshot,
  useUnpublishReportSnapshot,
  type ReportSection,
  type ReportSnapshot,
  type ReportSnapshotContent,
} from '../../../lib/api/queries';
import {
  asPendingNoteInsertionsProblem,
  type PendingNoteInsertionsProblem,
} from '../../../lib/api/formNotes';
import { ReportPreviewModal } from './ReportPreviewModal';

/**
 * "Preview Versions" modal — lists every saved snapshot, most recent
 * first. Clicking a snapshot loads its frozen content and opens the
 * existing ReportPreviewModal with the historical view.
 */
export function VersionListModal({
  tenantId,
  auditId,
  sections,
  onClose,
  onReviewPending,
}: {
  tenantId: string;
  auditId: string;
  /** The live report's sections, to name the ones holding pending form notes. */
  sections: ReportSection[];
  onClose: () => void;
  /** Slice 19c — go to a section to review its pending form-note blocks. */
  onReviewPending: (sectionKey: string) => void;
}) {
  const snapshots = useReportSnapshots(tenantId, auditId);
  const publish = usePublishReportSnapshot(tenantId, auditId);
  const unpublish = useUnpublishReportSnapshot(tenantId, auditId);
  const [previewing, setPreviewing] = useState<ReportSnapshotContent | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Slice 19c — publishing is blocked (not just warned) while the snapshot
  // holds form-note blocks nobody has kept.
  const [blocked, setBlocked] = useState<PendingNoteInsertionsProblem | null>(null);

  const onPick = async (snapshotId: string) => {
    setErr(null);
    setLoadingId(snapshotId);
    try {
      const content = await getReportSnapshotContent(auditId, snapshotId);
      setPreviewing(content);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const detail = (e as { detail?: string })?.detail;
      setErr(detail ?? (status ? `Request failed (${status})` : 'Request failed'));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
           onClick={onClose}>
        <div className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden flex flex-col"
             onClick={e => e.stopPropagation()}>
          <header className="flex items-center justify-between px-5 py-3 border-b border-gray-200
                             bg-gray-50">
            <h2 className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
              <History className="w-5 h-5 text-accent-alt-600" aria-hidden />
              Saved report versions
            </h2>
            <button type="button" onClick={onClose}
                    className="text-gray-500 hover:bg-gray-100 rounded-md w-8 h-8
                               inline-flex items-center justify-center">
              <X className="w-5 h-5" aria-hidden />
            </button>
          </header>

          <div className="max-h-[60vh] overflow-y-auto">
            {snapshots.isLoading && (
              <p className="px-5 py-4 text-sm text-gray-500">Loading versions…</p>
            )}
            {!snapshots.isLoading && !snapshots.data?.length && (
              <p className="px-5 py-6 text-sm text-gray-500">
                No saved versions yet. Click <strong>Save version</strong> on the report
                page to capture the current state.
              </p>
            )}
            {err && (
              <p className="px-5 py-2 text-sm text-danger-500">{err}</p>
            )}
            {blocked && (
              <PendingNotesPanel problem={blocked}
                                 sections={sections}
                                 onReview={onReviewPending} />
            )}
            <ul className="divide-y divide-gray-100">
              {snapshots.data?.map(s => (
                <SnapshotRow
                  key={s.id}
                  snapshot={s}
                  isBusy={loadingId === s.id
                       || publish.isPending
                       || unpublish.isPending}
                  onPreview={() => onPick(s.id)}
                  onPublish={async () => {
                    setErr(null);
                    setBlocked(null);
                    try {
                      await publish.mutateAsync(s.id);
                    } catch (e) {
                      const problem = asPendingNoteInsertionsProblem(e);
                      if (problem) setBlocked(problem);
                      else setErr(publishErrMessage(e));
                    }
                  }}
                  onUnpublish={async () => {
                    setErr(null);
                    const ok = window.confirm(
                      'Retract this report from the client?\n\n' +
                      'The client will no longer be able to view or ' +
                      're-download it. Any copies they already downloaded ' +
                      'remain theirs.');
                    if (!ok) return;
                    try {
                      await unpublish.mutateAsync(s.id);
                    } catch (e) {
                      setErr(publishErrMessage(e));
                    }
                  }}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>

      {previewing && (
        <ReportPreviewModal
          tenantId={tenantId}
          auditId={auditId}
          source={{ kind: 'snapshot', content: previewing }}
          onClose={() => setPreviewing(null)}
        />
      )}
    </>
  );
}

// -----------------------------------------------------------------------

function SnapshotRow({
  snapshot,
  isBusy,
  onPreview,
  onPublish,
  onUnpublish,
}: {
  snapshot: ReportSnapshot;
  isBusy: boolean;
  onPreview: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
}) {
  const state = snapshotState(snapshot);
  return (
    <li className="px-5 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 truncate">
              {snapshot.label ?? 'Unlabelled version'}
            </p>
            <StateChip state={state} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Saved {new Date(snapshot.createdAtUtc).toLocaleString()}
            {' '}· {snapshot.sectionCount} section{snapshot.sectionCount === 1 ? '' : 's'}
          </p>
          {state === 'published' && snapshot.publishedAtUtc && (
            <p className="text-xs text-brand-600 mt-0.5">
              Visible to the client since{' '}
              {new Date(snapshot.publishedAtUtc).toLocaleString()}
            </p>
          )}
          {state === 'retracted' && snapshot.unpublishedAtUtc && (
            <p className="text-xs text-warning-700 mt-0.5">
              Retracted on {new Date(snapshot.unpublishedAtUtc).toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button type="button"
                  onClick={onPreview}
                  disabled={isBusy}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                             text-gray-700 bg-white border border-gray-300 rounded-md
                             hover:bg-gray-50 transition-colors disabled:opacity-50">
            <Eye className="w-3.5 h-3.5" aria-hidden /> Preview
          </button>

          {state === 'draft' && (
            <button type="button"
                    onClick={onPublish}
                    disabled={isBusy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                               text-white bg-brand-600 hover:bg-brand-700 rounded-md
                               transition-colors disabled:opacity-50">
              <Send className="w-3.5 h-3.5" aria-hidden /> Publish
            </button>
          )}
          {state === 'published' && (
            <button type="button"
                    onClick={onUnpublish}
                    disabled={isBusy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                               text-warning-700 bg-warning-50 border border-warning-200
                               hover:bg-warning-100 rounded-md transition-colors
                               disabled:opacity-50">
              <Undo2 className="w-3.5 h-3.5" aria-hidden /> Unpublish
            </button>
          )}
          {state === 'retracted' && (
            <button type="button"
                    onClick={onPublish}
                    disabled={isBusy}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                               text-white bg-brand-600 hover:bg-brand-700 rounded-md
                               transition-colors disabled:opacity-50">
              <Send className="w-3.5 h-3.5" aria-hidden /> Republish
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function StateChip({ state }: { state: 'draft' | 'published' | 'retracted' }) {
  switch (state) {
    case 'draft':
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold
                         uppercase tracking-wide text-gray-600 bg-gray-100 rounded">
          Draft
        </span>
      );
    case 'published':
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold
                         uppercase tracking-wide text-brand-700 bg-brand-50 rounded">
          <CheckCircle2 className="w-3 h-3" aria-hidden />
          Published
        </span>
      );
    case 'retracted':
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold
                         uppercase tracking-wide text-warning-700 bg-warning-50 rounded">
          Retracted
        </span>
      );
  }
}

/** Extract a user-friendly error message from an ApiError-shaped throw. */
/**
 * Why a publish was refused: form-note blocks in this version that nobody
 * has kept. The version's content is frozen, so after reviewing the author
 * saves a new version and publishes that.
 */
function PendingNotesPanel({
  problem, sections, onReview,
}: {
  problem: PendingNoteInsertionsProblem;
  sections: ReportSection[];
  onReview: (sectionKey: string) => void;
}) {
  const titleOf = (key: string) => sections.find(s => s.key === key)?.title ?? 'Unknown section';
  const first = problem.pending[0];
  return (
    <div className="mx-5 my-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
      <p className="text-sm font-semibold text-amber-900">{problem.title}</p>
      <p className="text-sm text-amber-900 mt-1">
        This version can't be published while {problem.pending.length === 1
          ? 'a form-note block is'
          : `${problem.pending.length} form-note blocks are`} awaiting review.
        Keep or remove each one, then save a new version and publish that.
      </p>
      <ul className="mt-2 space-y-1">
        {problem.pending.map(p => (
          <li key={p.insertionId}>
            <button type="button" onClick={() => onReview(p.sectionKey)}
                    className="inline-flex items-start gap-1.5 text-left text-sm text-amber-900
                               hover:underline">
              <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
              <span>
                <span className="font-medium">{titleOf(p.sectionKey)}</span>
                {p.criterionCode && <> · {p.criterionCode}</>}
                {p.itemText && <> · {p.itemText}</>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {first && (
        <button type="button" onClick={() => onReview(first.sectionKey)}
                className="mt-3 px-3 py-1.5 text-sm font-medium text-white bg-accent-alt-600
                           hover:bg-accent-alt-700 rounded-md">
          Review now
        </button>
      )}
    </div>
  );
}

function publishErrMessage(err: unknown): string {
  const e = err as { body?: unknown; status?: number };
  if (typeof e.body === 'string' && e.body.length > 0) return e.body;
  if (e.status === 409) return "Snapshot is already in that state.";
  if (e.status === 404) return "Snapshot not found.";
  return "Request failed. Try again.";
}
